// Meta tag helpers for SEO — generates title + OG + Twitter Card meta tags
// Compatible with TanStack Start head() return format

import type { BlogPost } from "~/content/blog";
import { getBlogUrl } from "~/lib/blog-url";

const SITE_URL = "https://alchema.eu";
const SITE_NAME = "Alchema";

export interface MetaConfig {
  title: string;
  description: string;
  url?: string;
  type?: string;
  image?: string;
  locale?: string;
  noindex?: boolean;
}

type MetaTag =
  | { title: string }
  | { charSet: string }
  | { name: string; content: string }
  | { property: string; content: string };

// ── Generic meta builder ─────────────────────────────────────────────────────

export function buildMeta(config: MetaConfig): MetaTag[] {
  const tags: MetaTag[] = [
    { title: config.title },
    { name: "description", content: config.description },
    // Open Graph
    { property: "og:title", content: config.title },
    { property: "og:description", content: config.description },
    { property: "og:type", content: config.type ?? "website" },
    { property: "og:site_name", content: SITE_NAME },
  ];

  if (config.url) {
    const fullUrl = config.url.startsWith("http")
      ? config.url
      : `${SITE_URL}${config.url}`;
    tags.push({ property: "og:url", content: fullUrl });
  }

  if (config.image) {
    const fullImage = config.image.startsWith("http")
      ? config.image
      : `${SITE_URL}${config.image}`;
    tags.push({ property: "og:image", content: fullImage });
  } else {
    // Default OG image for social sharing previews
    tags.push({ property: "og:image", content: `${SITE_URL}/og-default.png` });
  }

  if (config.locale) {
    tags.push({ property: "og:locale", content: config.locale });
  }

  // Twitter Card — always use summary_large_image since we always have an OG image
  const twitterImage = config.image
    ? (config.image.startsWith("http") ? config.image : `${SITE_URL}${config.image}`)
    : `${SITE_URL}/og-default.png`;
  tags.push(
    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:title", content: config.title },
    { name: "twitter:description", content: config.description },
    { name: "twitter:image", content: twitterImage },
  );

  if (config.noindex) {
    tags.push({ name: "robots", content: "noindex, nofollow" });
  }

  return tags;
}

// ── Article-specific meta builder ────────────────────────────────────────────

export function buildArticleMeta(post: BlogPost): MetaTag[] {
  const base = buildMeta({
    title: `${post.title} | Alchema Blog`,
    description: post.description,
    url: getBlogUrl({ locale: post.locale, pageType: "editorial", slug: post.slug }).path,
    type: "article",
    image: post.coverImage,
    locale: post.locale === "de" ? "de_DE" : "en_US",
  });

  // Add article-specific meta
  base.push(
    { property: "article:published_time", content: post.publishedAt },
    { property: "article:author", content: post.author },
  );

  if (post.updatedAt) {
    base.push({ property: "article:modified_time", content: post.updatedAt });
  }

  for (const tag of post.tags) {
    base.push({ property: "article:tag", content: tag });
  }

  return base;
}

// ── Simple page meta builder ─────────────────────────────────────────────────

export function buildPageMeta(page: {
  title: string;
  description: string;
  path: string;
}): MetaTag[] {
  return buildMeta({
    title: `${page.title} | ${SITE_NAME}`,
    description: page.description,
    url: page.path,
  });
}
